import { useCallback, useMemo, useRef, useState } from 'react';

export interface Selection {
  /** Only ids still present in the list — see the pruning note below. */
  selected: Set<string>;
  /** The selected ids in list order, which is the order bulk actions run in. */
  ids: string[];
  count: number;
  has: (id: string) => boolean;
  /** Pass `shift` to extend from the last row touched. */
  toggle: (id: string, opts?: { shift?: boolean }) => void;
  setAll: (checked: boolean) => void;
  clear: () => void;
  allSelected: boolean;
  someSelected: boolean;
}

const EMPTY: ReadonlySet<string> = new Set();

/** The raw set intersected with the ids that still exist. */
export function pruneSelection(raw: ReadonlySet<string>, allIds: readonly string[]): Set<string> {
  if (raw.size === 0) return new Set<string>();
  const live = new Set(allIds);
  const next = new Set<string>();
  for (const id of raw) if (live.has(id)) next.add(id);
  return next;
}

/**
 * The selection after a click on `id`. With an anchor and `shift`, the whole
 * range from the anchor is painted to match the ANCHOR's state, not the clicked
 * row's: that makes shift-clicking a block of ticked rows clear them, which is
 * what every file manager does and what you reach for after over-selecting.
 * Pure, so the hook's behaviour can be tested without a renderer.
 */
export function toggleSelection(
  prev: ReadonlySet<string>,
  allIds: readonly string[],
  id: string,
  anchor: string | null,
  shift: boolean,
): Set<string> {
  const next = new Set(prev);
  const from = shift ? anchor : null;
  const a = from === null ? -1 : allIds.indexOf(from);
  const b = allIds.indexOf(id);
  if (a !== -1 && b !== -1) {
    const on = prev.has(from as string);
    for (let i = Math.min(a, b); i <= Math.max(a, b); i++) {
      if (on) next.add(allIds[i]);
      else next.delete(allIds[i]);
    }
  } else if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

/**
 * Checkbox selection over a list of ids, handling the two things a bare `Set`
 * gets wrong:
 *
 * 1. **Pruning.** Rows disappear — deleted, filtered, refetched. A raw Set keeps
 *    holding their ids, so you get "5 selected" with four visible ticks and a
 *    bulk action that 404s on a ghost. Selection is therefore *derived* from the
 *    raw set intersected with the live ids on every read, rather than corrected
 *    in an effect (which would render one frame with the stale count).
 * 2. **Shift-click.** Ticking 30 of 40 rows one at a time is not a feature.
 *
 * `allIds` must be referentially stable between renders or the derived set is
 * rebuilt each time — `useMemo(() => data.map(t => t.id), [data])` off a query
 * result is the intended shape.
 */
export function useSelection(allIds: string[]): Selection {
  const [raw, setRaw] = useState<ReadonlySet<string>>(EMPTY);
  const anchor = useRef<string | null>(null);

  const selected = useMemo(() => pruneSelection(raw, allIds), [allIds, raw]);

  const toggle = useCallback(
    (id: string, opts?: { shift?: boolean }) => {
      setRaw((prev) => toggleSelection(prev, allIds, id, anchor.current, !!opts?.shift));
      anchor.current = id;
    },
    [allIds],
  );

  const setAll = useCallback(
    (checked: boolean) => {
      anchor.current = null;
      setRaw(checked ? new Set(allIds) : EMPTY);
    },
    [allIds],
  );

  const clear = useCallback(() => {
    anchor.current = null;
    setRaw(EMPTY);
  }, []);

  return {
    selected,
    ids: useMemo(() => allIds.filter((id) => selected.has(id)), [allIds, selected]),
    count: selected.size,
    has: (id) => selected.has(id),
    toggle,
    setAll,
    clear,
    allSelected: allIds.length > 0 && selected.size === allIds.length,
    someSelected: selected.size > 0,
  };
}
